import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
  EntityState,
} from '@reduxjs/toolkit';
import { AppState } from '.';
import {
  exceptionOf,
  Invoice,
  SerializedException,
} from '../models';
import { InvoiceService } from '../services';
import { RequestErrorPolicy } from '../services/error-presentation';

export const INVOICE_FEATURE_KEY = 'invoices';

interface InvoiceState extends EntityState<Invoice> {
  loading: boolean;
  errors: SerializedException[];
}

const entityAdapter = createEntityAdapter<Invoice>({
  selectId: invoice => invoice.invoiceNumber!!,
});

const createInitialState = (): InvoiceState => entityAdapter.getInitialState({
  loading: false,
  errors: [],
});

export const GET_INVOICE_RECURLY_SUBSCRIPTION_NOT_EXISTED = 'invoice.recurly.subscription.not.existed';
export const GET_INVOICE_CANNOT_READ_RECURLY_SUBSCRIPTION = 'invoice.cannot.read.recurly.subscription';

export const doGetInvoiceByInvoiceNumbers = createAsyncThunk(
  'invoices/getByInvoiceNumber',
  async (
    request: Required<Parameters<typeof InvoiceService['getInvoices']>>[0] & RequestErrorPolicy,
    { rejectWithValue },
  ) => {
    try {
      const { errorPresentation, ...invoiceRequest } = request;
      const response = await InvoiceService.getInvoices(invoiceRequest, { errorPresentation });
      return response;
    } catch (e: any) {
      return rejectWithValue(exceptionOf(e).toJson());
    }
  },
);

const invoiceSlice = createSlice({
  name: INVOICE_FEATURE_KEY,
  initialState: createInitialState(),
  reducers: {
    doResetErrors(state) {
      state.errors = [];
    },
  },
  extraReducers: builder => {
    builder.addCase(doGetInvoiceByInvoiceNumbers.pending, state => {
      state.loading = true;
    });
    builder.addCase(doGetInvoiceByInvoiceNumbers.fulfilled, (state, action) => {
      state.loading = false;
      entityAdapter.upsertMany(state, action.payload);
    });
    builder.addCase(doGetInvoiceByInvoiceNumbers.rejected, (state, action) => {
      state.loading = false;
      state.errors.push(action.payload as SerializedException);
    });
  },
});

const selectInvoiceFeature = (state: AppState) => state[INVOICE_FEATURE_KEY];

export const selectErrors = () => createSelector(
  selectInvoiceFeature,
  invoiceState => invoiceState.errors,
);

export const selectInvoiceByNumbers = (invoiceNumbers: string[]) => createSelector(
  selectInvoiceFeature,
  invoiceState => invoiceNumbers.map(it => invoiceState.entities[it]),
);

export const {
  doResetErrors,
} = invoiceSlice.actions;

export default invoiceSlice.reducer;
